import type {
  CodeBlockRedlineEdit,
  CodeBlockRedlineEditError,
  CodeBlockRedlineInsert,
  CodeBlockRedlineItem,
  CodeBlockRedlineNote,
  CodeBlockRedlineRemove,
} from '@aline/common';
import { CodeBlockRedlineItemSchema } from '@aline/common';

export type ParsedRedlineItem = CodeBlockRedlineItem;

/**
 * Given a JSONL string, parse it into a list of redline edits, inserts,
 * removals, and per-paragraph errors. The shapes share a `type` discriminant
 * (`'redline-edit'` | `'redline-insert'` | `'redline-remove'` |
 * `'redline-edit-error'`).
 */
export function parseJsonlToRedlineList(jsonlString: string): ParsedRedlineItem[] {
  const lines = jsonlString.split('\n').filter(line => line.trim().length > 0);
  return lines.flatMap<ParsedRedlineItem>(line => {
    try {
      const parsed = JSON.parse(line);
      const result = CodeBlockRedlineItemSchema.safeParse(parsed);
      if (result.success) return [result.data];
      return [];
    } catch {
      return [];
    }
  });
}

export function getIsRedlineEdit(item: ParsedRedlineItem): item is CodeBlockRedlineEdit {
  return item.type === 'redline-edit';
}

export function getIsRedlineInsert(item: ParsedRedlineItem): item is CodeBlockRedlineInsert {
  return item.type === 'redline-insert';
}

export function getIsRedlineRemove(item: ParsedRedlineItem): item is CodeBlockRedlineRemove {
  return item.type === 'redline-remove';
}

export function getIsRedlineNote(item: ParsedRedlineItem): item is CodeBlockRedlineNote {
  return item.type === 'redline-note';
}

export function getIsRedlineEditError(item: ParsedRedlineItem): item is CodeBlockRedlineEditError {
  return item.type === 'redline-edit-error';
}
